import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssistantChatMessage } from '../data-access';

interface UseChatScrollManagerProps {
  messages: AssistantChatMessage[];
  containerHeight?: number;
  keepToBottom?: boolean;
  isStreaming?: boolean;
  statusText?: string | string[];
}

interface UseChatScrollManagerReturn {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  isOverflowing: boolean;
  isAtBottom: boolean;
  scrollToBottom: () => void;
}

export const useChatScrollManager = ({
  messages,
  containerHeight,
  keepToBottom = false,
  isStreaming = false,
  statusText,
}: UseChatScrollManagerProps): UseChatScrollManagerReturn => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const prevMessagesLengthRef = useRef(0);
  const prevAssistantSignatureRef = useRef<string | null>(null);

  const checkOverflow = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = messagesContainerRef.current;
      const isOverflowing = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px tolerance

      setIsOverflowing(isOverflowing);
      setIsAtBottom(isAtBottom);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(checkOverflow, 100);
  }, [checkOverflow]);

  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      const newMessage = messages[messages.length - 1];
      const isNewAIMessage = newMessage?.role === 'assistant';
      const isNewUserMessage = newMessage?.role === 'user';

      if (isNewUserMessage) {
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      } else if (isNewAIMessage) {
        setTimeout(
          () => {
            scrollToBottom();
          },
          keepToBottom ? 50 : 100
        ); // allow content to render before scrolling
      }
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, scrollToBottom, keepToBottom]);

  const assistantSignature = useMemo(() => {
    if (!messages.length) return null;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role !== 'assistant') return null;
    const { messageId, content } = lastMessage;
    const title =
      'title' in lastMessage && typeof lastMessage.title === 'string' ? lastMessage.title : '';
    const statusTextStr = Array.isArray(statusText) ? statusText.join('|') : (statusText ?? '');
    return `${messageId ?? 'assistant'}|${title}|${content ?? ''}|${statusTextStr}`;
  }, [messages, statusText]);

  useEffect(() => {
    if (!assistantSignature) {
      prevAssistantSignatureRef.current = null;
      return;
    }

    if (assistantSignature !== prevAssistantSignatureRef.current) {
      const timeout = window.setTimeout(
        () => {
          scrollToBottom();
        },
        isStreaming || keepToBottom ? 50 : 100
      );

      prevAssistantSignatureRef.current = assistantSignature;

      return () => window.clearTimeout(timeout);
    }
  }, [assistantSignature, scrollToBottom, isStreaming, keepToBottom]);

  useEffect(() => {
    checkOverflow();

    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      const handleScroll = () => checkOverflow();
      messagesContainer.addEventListener('scroll', handleScroll);
      return () => messagesContainer.removeEventListener('scroll', handleScroll);
    }
  }, [messages, containerHeight, checkOverflow]);

  useEffect(() => {
    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkOverflow]);

  return {
    messagesContainerRef,
    messagesEndRef,
    isOverflowing,
    isAtBottom,
    scrollToBottom,
  };
};
